import { cn } from 'cn'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Button } from './button'
import { Dialog, DialogContent } from './dialog'

interface DatePickerProps {
  open: boolean
  /** currently confirmed value */
  value?: Date | null
  /** earliest selectable day (inclusive) */
  min?: Date
  /** latest selectable day (inclusive) */
  max?: Date
  /** when true, renders a time picker section below the day grid */
  showTime?: boolean
  onSelect: (date: Date) => void
  onClose: () => void
}

/** Single letters, as production labels them — S and T each appear twice. */
const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S']
const SHORT_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]
const SHORT_WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/** The header's own format, e.g. "Fri, Oct 2" — no year, no padded day. */
function formatHeaderDate(d: Date): string {
  return `${SHORT_WEEKDAYS[d.getDay()]}, ${SHORT_MONTHS[d.getMonth()]} ${d.getDate()}`
}
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

function startOfDay(d: Date): Date {
  const c = new Date(d)
  c.setHours(0, 0, 0, 0)
  return c
}
function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}
function firstOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1)
}
function periodOf(d: Date): 'AM' | 'PM' {
  return d.getHours() >= 12 ? 'PM' : 'AM'
}

/**
 * Pixel-faithful month-grid date picker (Figma "Date Picker" screen).
 * Cells outside [min, max] render disabled; the confirmed/tentative day renders
 * on brand blue. Selection is tentative until "Okay".
 *
 * When `showTime=true`, a time section (hour, minute, AM/PM) is rendered below
 * the day grid and the selected Date includes the chosen time.
 */
function DatePicker({
  open,
  value,
  min,
  max,
  showTime,
  onSelect,
  onClose,
}: Readonly<DatePickerProps>) {
  const today = useMemo(() => startOfDay(new Date()), [])
  const initialMonth = value ?? min ?? today
  const [viewMonth, setViewMonth] = useState<Date>(firstOfMonth(initialMonth))
  const [tentative, setTentative] = useState<Date | null>(value ?? null)

  // Time state — only used when showTime=true
  const [hour, setHour] = useState<number>(value ? value.getHours() % 12 || 12 : 10)
  const [minute, setMinute] = useState<number>(value ? value.getMinutes() : 0)
  const [period, setPeriod] = useState<'AM' | 'PM'>(value ? periodOf(value) : 'AM')

  // Re-sync when the dialog is (re)opened.
  useEffect(() => {
    if (open) {
      setTentative(value ?? null)
      setViewMonth(firstOfMonth(value ?? min ?? today))
      if (showTime && value) {
        setHour(value.getHours() % 12 || 12)
        setMinute(value.getMinutes())
        setPeriod(periodOf(value))
      }
    }
  }, [open, value, min, today, showTime])

  const minDay = min ? startOfDay(min) : null
  const maxDay = max ? startOfDay(max) : null

  const isSelectable = (day: Date) => {
    if (minDay && day < minDay) return false
    if (maxDay && day > maxDay) return false
    return true
  }

  const year = viewMonth.getFullYear()
  const month = viewMonth.getMonth()
  const leadingBlanks = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()

  const cells: (Date | null)[] = []
  for (let i = 0; i < leadingBlanks; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d))

  const canPrev = !minDay || firstOfMonth(minDay) < viewMonth
  const canNext = !maxDay || new Date(year, month + 1, 1) <= maxDay

  const gotoMonth = (delta: number) => setViewMonth(new Date(year, month + delta, 1))

  const handleOkay = () => {
    if (!tentative) return
    if (showTime) {
      const d = new Date(tentative)
      let h = hour % 12
      if (period === 'PM') h += 12
      d.setHours(h, minute, 0, 0)
      onSelect(d)
    } else {
      onSelect(tentative)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-tour="date-picker-dialog"
        // The `showTime` variant is 616-660px tall (6-row months add 44px), which
        // does not fit a 667px phone and leaves a guided coachmark card nowhere to
        // sit above it — Fix List row 7. Capping it keeps rect.top at 248 on every
        // viewport, which is the room the compact card needs.
        className="top-auto bottom-3 max-h-[calc(100svh-260px)] w-85 max-w-[calc(100vw-32px)] translate-y-0 gap-0 overflow-y-auto rounded-3xl p-0"
        showCloseButton={false}
      >
        {/* Header: what is being picked, then what is picked so far. */}
        <div className="px-6 pb-4 pt-5">
          <p className="text-sm text-foreground">Select date</p>
          <p className="mt-3 text-3xl text-foreground">
            {tentative ? formatHeaderDate(tentative) : '—'}
          </p>
        </div>
        <div className="border-t border-border" />

        {/* Month navigation */}
        <div className="flex items-center justify-between px-6 py-3">
          <span className="text-sm text-foreground">
            {MONTHS[month]} {year}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label="Previous month"
              onClick={() => gotoMonth(-1)}
              disabled={!canPrev}
              className="flex size-9 items-center justify-center rounded-full text-foreground transition-colors hover:bg-accent disabled:pointer-events-none disabled:opacity-30"
            >
              <ChevronLeft className="size-5" />
            </button>
            <button
              type="button"
              aria-label="Next month"
              onClick={() => gotoMonth(1)}
              disabled={!canNext}
              className="flex size-9 items-center justify-center rounded-full text-foreground transition-colors hover:bg-accent disabled:pointer-events-none disabled:opacity-30"
            >
              <ChevronRight className="size-5" />
            </button>
          </div>
        </div>

        {/* Weekday header. Keyed by position: S and T each occur twice. */}
        <div className="grid grid-cols-7 gap-1 px-4">
          {WEEKDAYS.map((w, i) => (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length weekday row // NOSONAR
              key={`weekday-${i}`}
              className="flex h-8 items-center justify-center text-sm text-foreground"
            >
              {w}
            </div>
          ))}
        </div>

        {/* Day grid */}
        <div data-tour="date-picker-grid" className="mt-1 grid grid-cols-7 gap-1 px-4">
          {cells.map((day, i) => {
            if (!day) {
              // biome-ignore lint/suspicious/noArrayIndexKey: fixed-position blank cell // NOSONAR
              return <div key={`blank-${year}-${month}-${i}`} className="h-10" />
            }
            const selectable = isSelectable(day)
            const isSelected = tentative && sameDay(day, tentative)
            const isToday = sameDay(day, today)
            return (
              <button
                key={day.toISOString()}
                type="button"
                disabled={!selectable}
                onClick={() => setTentative(day)}
                className={cn(
                  'flex h-10 items-center justify-center rounded-full text-sm transition-colors',
                  isSelected && 'bg-primary font-semibold text-primary-foreground',
                  !isSelected && selectable && 'text-foreground hover:bg-accent',
                  !isSelected && isToday && selectable && 'ring-1 ring-primary',
                  !selectable && 'text-date-disabled pointer-events-none',
                )}
              >
                {day.getDate()}
              </button>
            )
          })}
        </div>

        {/* Time section — only when showTime=true */}
        {showTime && (
          <>
            <div className="mt-4 border-t border-border" />
            <div className="flex flex-col gap-4 px-6 pt-4">
              {/* Time label */}
              <div className="flex items-center">
                <span className="text-sm font-bold text-foreground">Time</span>
                <span className="ml-0.5 text-sm font-bold text-destructive">*</span>
              </div>
              {/* Controls */}
              <div className="flex items-center gap-4">
                {/* Hour + Minute inputs */}
                <div className="flex flex-1 gap-2">
                  <input
                    type="number"
                    min={1}
                    max={12}
                    value={hour}
                    onChange={(e) => {
                      const v = Number(e.target.value)
                      if (v >= 1 && v <= 12) setHour(v)
                    }}
                    className="h-10 flex-1 rounded-full border border-border text-center text-sm font-medium text-foreground focus:outline-none focus:border-primary"
                  />
                  <input
                    type="number"
                    min={0}
                    max={59}
                    value={String(minute).padStart(2, '0')}
                    onChange={(e) => {
                      const v = Number(e.target.value)
                      if (v >= 0 && v <= 59) setMinute(v)
                    }}
                    className="h-10 flex-1 rounded-full border border-border text-center text-sm font-medium text-foreground focus:outline-none focus:border-primary"
                  />
                </div>
                {/* AM/PM toggle */}
                <div className="flex items-center gap-3 rounded-full bg-secondary px-2 py-1 h-12">
                  <button
                    type="button"
                    onClick={() => setPeriod('AM')}
                    className={cn(
                      'px-4 py-1',
                      period === 'AM'
                        ? 'bg-primary text-primary-foreground rounded-full text-base font-bold'
                        : 'text-muted-foreground text-base',
                    )}
                  >
                    AM
                  </button>
                  <button
                    type="button"
                    onClick={() => setPeriod('PM')}
                    className={cn(
                      'px-4 py-1',
                      period === 'PM'
                        ? 'bg-primary text-primary-foreground rounded-full text-base font-bold'
                        : 'text-muted-foreground text-base',
                    )}
                  >
                    PM
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Footer: text actions, right-aligned, as production draws them. */}
        <div className="flex justify-end gap-2 px-4 pb-4 pt-5">
          <Button
            variant="ghost"
            onClick={onClose}
            className="h-11 px-5 text-base font-medium text-primary hover:bg-accent"
            data-tour="date-picker-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="ghost"
            onClick={handleOkay}
            disabled={!tentative}
            className="h-11 px-5 text-base font-medium text-primary hover:bg-accent"
            data-tour="date-picker-okay"
          >
            Okay
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export { DatePicker }
